import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of CartSlice — plan §5.2. State and all 3 reducers are IDENTICAL across the
 * apps; the only difference is that web registers the logout `extraReducers` and mobile does
 * not, which is why this is still a split rather than a `shared.ts`-owns-everything module.
 *
 * ✅ **HAZARD 1 RESOLVED — this slice is now named `"cart"`, not `"checkout"`.**
 * It previously shared `"checkout"` with web's `CheckoutSlice` AND with the checkout slice
 * inside `@dotpe/rista-pos-common` (which mobile's `CheckoutSlice` re-exports), so FOUR
 * slices carried one action-type prefix. Action types are `<name>/<reducerKey>`, so any
 * reducer key added to two of them would have made a single dispatch reduce into both,
 * silently. Renaming removes the whole class of bug on both platforms.
 *
 * Safe to rename because `name` is the ACTION-TYPE PREFIX ONLY — not the persist key.
 * redux-persist whitelists the `combineReducers` key (`cartStore`), and `cartStore` is in
 * NEITHER app's whitelist, so nothing was persisted under the old prefix and no migration is
 * needed. Verified before the change: no string-literal `"checkout/…"` action type anywhere
 * in either app or in core, and nothing matches on an action-type prefix — every dispatch
 * goes through the generated creators below, which pick the new prefix up automatically.
 *
 * ⚠️ `DiscountState` is a MISNOMER — it is CartSlice's state, not DiscountSlice's. Preserved
 * because it is exported surface in both apps. Do not "fix" it.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger.
 */
export interface DiscountState {
  cartModalState: boolean,
  resetServiceMode: boolean
}

export const sharedInitialState: DiscountState = {
  cartModalState: false,
  resetServiceMode: false
};

/** Identical in both apps. */
export const sharedReducers = {
  toggleCartState: (state: DiscountState, action: PayloadAction<any>) => {
    state.cartModalState = action.payload;
  },
  resetServiceModeData: (state: DiscountState, action: PayloadAction<any>) => {
    state.resetServiceMode = action.payload;
  },
  updateCartSlice: (state: DiscountState, action: PayloadAction<any>) => {
    return action.payload;
  },
};
